import Phaser from 'phaser';
import { getNextTile, type Direction, type TileCoord } from '../logic/movement';

const MOVE_DURATION_MS = 160;

/**
 * Column order inside public/atlases/player-cozy.png (the processed cozy
 * reader sheet — see art/output/p1-player-draft-1k-v2-cropped.sheet.json):
 * 4 columns, one per facing, left to right.
 */
const DIRECTION_COLUMN: Record<Direction, number> = {
  down: 0,
  left: 1,
  right: 2,
  up: 3,
};

const FRAMES_PER_ROW = 4;
/**
 * The cozy sheet has two rows per facing — a small bordered preview (row 0)
 * and a larger detailed turnaround (row 1) — not an idle/walk-cycle split.
 * There's no walk-cycle art yet (tracked in HANDOVER.md as follow-up
 * polish), so every facing always shows its one detailed pose, whether the
 * player is idle or mid-step.
 */
const DETAIL_ROW = 1;

function frameIndex(direction: Direction): number {
  return DETAIL_ROW * FRAMES_PER_ROW + DIRECTION_COLUMN[direction];
}

export class Player extends Phaser.GameObjects.Sprite {
  private _tile: TileCoord;
  private _facing: Direction = 'down';
  private _moving = false;

  constructor(scene: Phaser.Scene, startTile: TileCoord, private readonly tileSize: number) {
    const x = startTile.x * tileSize + tileSize / 2;
    // Sprite is 16x32 (taller than one tile) so its feet, not its centre,
    // should land on the tile grid.
    const y = startTile.y * tileSize + tileSize;
    super(scene, x, y, 'player-atlas', frameIndex('down'));
    this.setOrigin(0.5, 1);
    this._tile = { ...startTile };
    scene.add.existing(this);
  }

  get tile(): TileCoord {
    return this._tile;
  }

  get facing(): Direction {
    return this._facing;
  }

  get isMoving(): boolean {
    return this._moving;
  }

  getFacingTile(): TileCoord {
    return getNextTile(this._tile, this._facing);
  }

  /** Turn to face a direction without moving (used when a move is blocked). */
  face(direction: Direction): void {
    this._facing = direction;
    if (!this._moving) {
      this.setFrame(frameIndex(direction));
    }
  }

  /**
   * Begin a tile-stepping move toward `direction`. The caller is responsible
   * for having already validated the destination tile with canMoveTo().
   */
  startMove(direction: Direction, onComplete?: () => void): void {
    if (this._moving) return;

    this._facing = direction;
    this._moving = true;
    const nextTile = getNextTile(this._tile, direction);
    this._tile = nextTile;

    const targetX = nextTile.x * this.tileSize + this.tileSize / 2;
    const targetY = nextTile.y * this.tileSize + this.tileSize;

    // No walk-cycle frames in the cozy sheet yet (see frameIndex's docs) —
    // just face the direction being moved into for the whole tween.
    this.setFrame(frameIndex(direction));

    this.scene.tweens.add({
      targets: this,
      x: targetX,
      y: targetY,
      duration: MOVE_DURATION_MS,
      ease: 'Linear',
      onComplete: () => {
        this._moving = false;
        this.setFrame(frameIndex(direction));
        onComplete?.();
      },
    });
  }
}
